import { useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { CopyField, KeyValueGrid, SectionCard } from '@/components/DataList';
import { account } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Button, Empty, ErrorBox, Loading } from '@/components/ui';
import { str } from '@/utils/format';

function unwrapData(payload: JsonMap): JsonMap {
  return payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? (payload.data as JsonMap)
    : payload;
}

function enabledLabel(value: unknown): string {
  if (value === true || value === 1 || value === '1') return 'مفعّل';
  if (value === false || value === 0 || value === '0') return 'غير مفعّل';
  return str(value);
}

function resolveApiKey(data: JsonMap, revealedToken: string): string {
  const plain = str(revealedToken || data.api_key || data.token || data.key, '');
  if (plain) return plain;
  const prefix = str(data.token_prefix, '');
  return prefix ? `${prefix}…` : '';
}

export function PartnerApiScreen() {
  const apiInfo = useAsync(() => account.apiInfo(), unwrapData);
  const pending = useAsync(
    () => account.pendingOrders(),
    (p) => pickList(p, 'orders', 'items', 'data'),
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [revealedToken, setRevealedToken] = useState('');

  async function regen() {
    setBusy(true);
    const res = await account.regenerateApi();
    setBusy(false);
    if (res.success) {
      const data = unwrapData(res);
      setRevealedToken(str(data.token, ''));
      setMsg('تم تجديد مفتاح API. احفظه الآن؛ لن يُعرض بالكامل مرة أخرى.');
    } else {
      setMsg(errorMessage(res));
    }
    apiInfo.reload();
  }

  async function submitPending(id: string | number) {
    setBusy(true);
    const res = await account.submitPending({ order_id: id, id });
    setBusy(false);
    setMsg(res.success ? 'تم إرسال الطلب.' : errorMessage(res));
    pending.reload();
  }

  async function cancelPending(id: string | number) {
    setBusy(true);
    const res = await account.cancelPending({ order_id: id, id });
    setBusy(false);
    setMsg(res.success ? 'تم إلغاء الطلب.' : errorMessage(res));
    pending.reload();
  }

  if (apiInfo.loading) return <Loading />;
  if (apiInfo.error) return <ErrorBox message={apiInfo.error} onRetry={apiInfo.reload} />;

  const data = apiInfo.data ?? {};
  const apiKey = resolveApiKey(data, revealedToken);
  const orders = pending.data ?? [];
  // Docs HTML lives on the SPA/dashboard host; prefer same-origin so Capacitor/web open it.
  const docsUrl =
    typeof window !== 'undefined'
      ? new URL('/api-docs.html', window.location.origin).href
      : str(data.documentation_url, 'https://dashboard.eshhanly.com/api-docs.html');

  return (
    <ScreenFrame mode="customer" title="API" subtitle="مفاتيح وطلبات Partner API">
      <SectionCard title="الاتصال">
        <div className="api-connection-grid">
          <div className="api-connection-card">
            <KeyValueGrid
              items={[
                { label: 'الحالة', value: enabledLabel(data.enabled ?? data.access ?? data.status) },
                {
                  label: 'Base URL',
                  value: <code className="inline-code">{str(data.base_url ?? data.endpoint)}</code>,
                },
                ...(data.access_mode
                  ? [{ label: 'وضع الوصول', value: str(data.access_mode) }]
                  : []),
              ]}
            />
          </div>
        </div>
        <CopyField label="API Key" value={apiKey} badge="KEY" />
        {revealedToken ? (
          <p className="ok">انسخ المفتاح الآن. بعد مغادرة الصفحة لن يظهر إلا بادئة الرمز.</p>
        ) : data.has_token || data.token_prefix ? (
          <p className="muted">يُعرض الرمز الكامل مرة واحدة عند التجديد فقط. المعروض حالياً بادئة الرمز.</p>
        ) : null}
        <p className="muted">
          التوثيق:{' '}
          <a href={docsUrl} target="_blank" rel="noopener noreferrer">
            فتح توثيق API
          </a>
        </p>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => void regen()}>
          تجديد المفتاح
        </Button>
      </SectionCard>

      <SectionCard title="طلبات معلّقة">
        {pending.loading ? (
          <Loading />
        ) : pending.error ? (
          <ErrorBox message={pending.error} onRetry={pending.reload} />
        ) : orders.length === 0 ? (
          <Empty title="لا توجد طلبات معلّقة" />
        ) : (
          <div className="list-stack">
            {orders.map((o) => {
              const id = o.id ?? o.order_id;
              return (
                <article key={str(id)} className="list-card">
                  <div className="list-row">
                    <div className="cell-stack">
                      <strong>{str(o.title ?? o.product_name ?? o.type, 'طلب')}</strong>
                      <small className="muted">{str(o.status)}</small>
                    </div>
                    <div className="btn-row">
                      <Button
                        type="button"
                        className="compact"
                        disabled={busy}
                        onClick={() => void submitPending(id as string | number)}
                      >
                        إرسال
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="compact"
                        disabled={busy}
                        onClick={() => void cancelPending(id as string | number)}
                      >
                        إلغاء
                      </Button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>

      {msg ? <p className="ok">{msg}</p> : null}
    </ScreenFrame>
  );
}
